import { useMemo } from 'react';
import { usePlugins } from '@/hooks/config/usePlugins';
import { getBuiltInEntityIdPreview } from '@/lib/resource-previews';
import type { ResolvedResourcePreview } from '@/lib/plugin-registry';

/**
 * Resolve the preview registration for a raw entity id's 3-letter prefix,
 * consulting plugin manifests first (`resourcePreviews[].idPrefixes`) and
 * falling back to the host built-ins (`agt`/`usr`/`tem`/`org`). Returns
 * `undefined` when no preview owns the prefix — callers render a plain,
 * copyable id badge in that case. Mirrors {@link useResourcePreviewRegistration}
 * but keyed by prefix rather than semantic kind.
 */
export function useEntityIdPreview(
  prefix: string | undefined,
): ResolvedResourcePreview | undefined {
  const { entityIdPreviews } = usePlugins();
  return useMemo(() => {
    if (!prefix) return undefined;
    return entityIdPreviews.get(prefix) ?? getBuiltInEntityIdPreview(prefix);
  }, [prefix, entityIdPreviews]);
}
